// 9fbef606107a605d69c0edbcd8029e5d
import throttle from 'lodash/throttle';
import React, {
  useRef,
  useMemo,
  useState,
  useEffect,
  forwardRef,
  useCallback,
  useImperativeHandle,
} from 'react';
import { Container, ScrollWrapper, Arrow } from '../styles/Carousel.style';

const Carousel = forwardRef(
  (
    {
      resetId,
      children,
      className = '',
      overrideGap = 0,
      overlapRatio = 0,
      overrideWidth = 0,
      showArrows = true,
      scrollBehavior = 'smooth',
      arrowVisibilityThreshold = 2,
      arrowComponent: CustomArrow = Arrow,
    },
    ref
  ) => {
    const scrollRef = useRef(null);
    const resizeObserverRef = useRef(null);

    const [arrowVisibility, setArrowVisibility] = useState({
      left: false,
      right: false,
    });

    /** -----------------------
     * Arrow Visibility Logic
     * ---------------------- */
    const checkArrowsVisibility = useCallback(() => {
      const container = scrollRef.current;
      if (!container || !showArrows) return;

      const maxScrollLeft = container.scrollWidth - container.clientWidth;

      if (maxScrollLeft <= 0) {
        setArrowVisibility({ left: false, right: false });
        return;
      }

      const currentScrollLeft = container.scrollLeft;

      const leftVisible = currentScrollLeft > arrowVisibilityThreshold;
      const rightVisible = currentScrollLeft < maxScrollLeft - arrowVisibilityThreshold;

      setArrowVisibility((prev) =>
        prev.left === leftVisible && prev.right === rightVisible
          ? prev
          : { left: leftVisible, right: rightVisible }
      );
    }, [showArrows, arrowVisibilityThreshold]);

    const throttledCheckArrowsVisibility = useMemo(
      () => throttle(() => checkArrowsVisibility(), 100, { leading: true, trailing: true }),
      [checkArrowsVisibility]
    );

    /** -----------------------
     * Scroll Helpers
     * ---------------------- */
    const calculateScrollAmount = useCallback(() => {
      const container = scrollRef.current;
      if (!container) return 0;

      if (overlapRatio > 0) {
        const viewportWidth = container.clientWidth;
        return Math.max(1, viewportWidth * (1 - overlapRatio));
      }

      const firstChild = container.firstElementChild;
      if (firstChild) {
        const itemWidth = firstChild.getBoundingClientRect().width;
        const gapVal = Number(overrideGap) > 0 ? overrideGap : 16;
        return itemWidth + gapVal;
      }

      return container.clientWidth;
    }, [overlapRatio, overrideGap]);

    const scrollToItem = useCallback(
      (direction = 'right') => {
        const container = scrollRef.current;
        if (!container) return;

        const scrollAmount = calculateScrollAmount();
        const delta = direction === 'right' ? scrollAmount : -scrollAmount;

        container.scrollBy({
          left: delta,
          behavior: scrollBehavior,
        });
      },
      [calculateScrollAmount, scrollBehavior]
    );

    const scrollLeft = useCallback(() => scrollToItem('left'), [scrollToItem]);
    const scrollRight = useCallback(() => scrollToItem('right'), [scrollToItem]);

    /** -----------------------
     * Imperative Handle API
     * ---------------------- */
    useImperativeHandle(
      ref,
      () => ({
        scrollToIndexIfNotFullyVisible: (index) => {
          const container = scrollRef.current;
          if (!container || !container.children[index]) return;

          const element = container.children[index];
          const elementRect = element.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();

          const isFullyVisible =
            elementRect.left >= containerRect.left && elementRect.right <= containerRect.right;

          if (isFullyVisible) return;

          const scrollPosition =
            element.offsetLeft - (container.clientWidth - elementRect.width) / 2;

          container.scrollTo({
            left: scrollPosition,
            behavior: scrollBehavior,
          });
        },
        scrollToStart: () => {
          scrollRef.current?.scrollTo({ left: 0, behavior: scrollBehavior });
        },
        scrollToEnd: () => {
          const container = scrollRef.current;
          if (container) {
            container.scrollTo({
              left: container.scrollWidth - container.clientWidth,
              behavior: scrollBehavior,
            });
          }
        },
        getCurrentScrollPosition: () => scrollRef.current?.scrollLeft || 0,
      }),
      [scrollBehavior]
    );

    /** -----------------------
     * Reset on resetId change
     * ---------------------- */
    useEffect(() => {
      const container = scrollRef.current;
      if (container && resetId !== undefined) {
        container.scrollLeft = 0;
        requestAnimationFrame(() => throttledCheckArrowsVisibility());
      }
    }, [resetId, throttledCheckArrowsVisibility]);

    /** -----------------------
     * Setup listeners / ResizeObserver
     * ---------------------- */
    useEffect(() => {
      if (typeof window === 'undefined') {
        return () => {};
      }

      const container = scrollRef.current;

      checkArrowsVisibility();

      if (container) container.addEventListener('scroll', throttledCheckArrowsVisibility);
      window.addEventListener('resize', throttledCheckArrowsVisibility);

      if ('ResizeObserver' in window && container) {
        resizeObserverRef.current = new ResizeObserver(throttledCheckArrowsVisibility);
        resizeObserverRef.current.observe(container);
      }

      return () => {
        if (container) container.removeEventListener('scroll', throttledCheckArrowsVisibility);
        window.removeEventListener('resize', throttledCheckArrowsVisibility);
        throttledCheckArrowsVisibility.cancel?.();
        resizeObserverRef.current?.disconnect();
      };
    }, [throttledCheckArrowsVisibility, checkArrowsVisibility]);

    /** -----------------------
     * Handle children changes
     * ---------------------- */
    useEffect(() => {
      const timeoutId = setTimeout(() => {
        throttledCheckArrowsVisibility();
      }, 50);

      return () => clearTimeout(timeoutId);
    }, [children, throttledCheckArrowsVisibility]);

    /** -----------------------
     * Memoized Arrow Component
     * ---------------------- */
    const MemoArrow = useMemo(() => React.memo(CustomArrow), [CustomArrow]);

    return (
      <Container
        className={className}
        data-testid="carousel-container"
        overrideWidth={Number(overrideWidth)}
      >
        {showArrows && (
          <MemoArrow
            left
            onClick={scrollLeft}
            aria-label="Scroll left"
            show={arrowVisibility.left}
            disabled={!arrowVisibility.left}
            aria-hidden={!arrowVisibility.left}
            className="media-carousel-left-arrow"
          />
        )}
        <ScrollWrapper
          tabIndex={0}
          role="region"
          ref={scrollRef}
          aria-label="Carousel"
          className="images-scroll-wrapper"
          overrideGap={Number(overrideGap)}
        >
          {children}
        </ScrollWrapper>
        {showArrows && (
          <MemoArrow
            onClick={scrollRight}
            aria-label="Scroll right"
            show={arrowVisibility.right}
            disabled={!arrowVisibility.right}
            aria-hidden={!arrowVisibility.right}
            className="media-carousel-right-arrow"
          />
        )}
      </Container>
    );
  }
);

Carousel.displayName = 'Carousel';

export default React.memo(Carousel);
